import { createDeck, type Card } from '../../engine/card.js';
import { findPileWithCard, type GameState, type Pile } from '../../engine/game-state.js';
import { applyMove, type Move } from '../../engine/moves.js';
import { shuffle } from '../../engine/shuffle.js';
import { createCardEl, setCardFace } from './card-sprite.js';
import { attachClassicDrag } from './drag.js';
import { createClassicHud } from './hud.js';
import { applyHudVisibility } from '../shared/hud-toggle.js';
import { createDailyCalendar } from '../shared/daily-calendar.js';
import { markCompleted, seedForDateStr } from '../shared/daily-record.js';
import { CARD_W, CARD_H, FAN_DOWN, type ClassicSpec } from './types.js';
import { runWinCascade, type WinCascade } from './win-cascade.js';

export interface MountOptions {
  /** root element to mount the game into. Required. */
  root: HTMLElement;
  /** title shown in the HUD */
  title: string;
}

export function mountClassic(spec: ClassicSpec, opts: MountOptions): void {
  const { root, title } = opts;
  applyHudVisibility();
  root.innerHTML = '';
  root.classList.add('cl2-root');

  // ── felt ──────────────────────────────────────────────────────────
  const felt = document.createElement('div');
  felt.className = 'cl2-felt';
  felt.style.width = `${spec.feltWidth}px`;
  felt.style.height = `${spec.feltHeight}px`;
  root.appendChild(felt);

  // History stack for undo (snapshots of GameState).
  const history: GameState[] = [];
  function pushHistory() { history.push(cloneState(state)); }
  function popHistory(): GameState | undefined { return history.pop(); }

  // ── state ─────────────────────────────────────────────────────────
  const deckFactory = spec.deckFactory ?? (() => createDeck());
  let state: GameState = spec.rules.setup(shuffle(deckFactory()));
  // YYYY-MM-DD when the current game is a daily deal (for the win record); null otherwise.
  let dailyDate: string | null = null;

  // ── DOM bookkeeping ───────────────────────────────────────────────
  const cardElById = new Map<string, HTMLDivElement>();

  // ── slot markers ──────────────────────────────────────────────────
  // Render an empty placeholder per pile so empty slots are visible. The
  // spec opts in/out via showSlotMarker.
  for (const pile of Object.values(state.piles)) {
    const show = spec.showSlotMarker ? spec.showSlotMarker(pile.id) : true;
    if (!show) continue;
    const slot = spec.slotForPile(pile.id);
    const marker = document.createElement('div');
    marker.className = 'cl2-slot';
    marker.style.left = `${slot.x}px`;
    marker.style.top = `${slot.y}px`;
    felt.appendChild(marker);
  }

  function targetForCard(pileId: string, indexInPile: number): { x: number; y: number; z: number } {
    const slot = spec.slotForPile(pileId);
    const pile = state.piles[pileId];
    const off = pile && spec.cardOffset ? spec.cardOffset(pile, indexInPile) : null;
    const dx = off?.dx ?? 0;
    const dy = off?.dy ?? 0;
    const fan = slot.fanOffset ?? FAN_DOWN;
    return {
      x: slot.x + dx,
      y: slot.y + dy + (slot.fanDown ? indexInPile * fan : 0),
      z: 10 + indexInPile, // base z above markers, +1 per stack position
    };
  }

  function placeCard(card: Card, pileId: string, indexInPile: number) {
    const el = cardElById.get(card.id)!;
    const t = targetForCard(pileId, indexInPile);
    el.style.left = `${t.x}px`;
    el.style.top = `${t.y}px`;
    el.style.zIndex = String(t.z);
    setCardFace(el, card);
  }

  function snapAll() {
    for (const pile of Object.values(state.piles)) {
      pile.cards.forEach((card, i) => placeCard(card, pile.id, i));
    }
  }

  function buildCards(cards: Card[]) {
    for (const card of cards) {
      const { el } = createCardEl(card);
      cardElById.set(card.id, el);
      felt.appendChild(el);
    }
  }

  buildCards(Object.values(state.piles).flatMap((p) => p.cards));
  snapAll();

  // ── HUD ───────────────────────────────────────────────────────────
  const hud = createClassicHud({
    root,
    title,
    statusText: () => spec.statusText?.(state) ?? null,
    onUndo: undo,
    onNewGame: () => newGame(),
    onDaily: () => dailyCalendar.open(),
  });

  const dailyCalendar = createDailyCalendar({
    variant: spec.rules.id,
    onPlay: (date) => startDaily(date),
  });

  function refreshUndo() { hud.setUndoEnabled(history.length > 0); }
  refreshUndo();

  // ── win/lose detection ────────────────────────────────────────────
  let winShown = false;
  let cascade: WinCascade | null = null;
  let dialogTimer: ReturnType<typeof setTimeout> | null = null;

  function cancelCascade() {
    cascade?.cancel();
    cascade = null;
    if (dialogTimer != null) {
      clearTimeout(dialogTimer);
      dialogTimer = null;
    }
  }

  function afterStateChange() {
    hud.recordMove();
    refreshUndo();
    if (spec.rules.isWon(state) && !winShown) {
      winShown = true;
      if (dailyDate) markCompleted(spec.rules.id, dailyDate);
      // Pull the cascade card list (variants override; default = all
      // foundations) and start the bouncing animation. Pop the dialog a
      // beat later so the cascade reads first.
      const ids = spec.cascadeCardIds
        ? spec.cascadeCardIds(state)
        : Object.values(state.piles)
            .filter((p) => p.type === 'foundation')
            .flatMap((p) => p.cards.map((c) => c.id));
      const cardEls = ids
        .map((id) => cardElById.get(id))
        .filter((el): el is HTMLDivElement => !!el);
      if (cardEls.length > 0) {
        cascade = runWinCascade({ root: felt, cards: cardEls });
      }
      dialogTimer = setTimeout(() => hud.showWin(), 2500);
    } else if (spec.rules.isLost?.(state)) {
      hud.refreshStatus();
      hud.showLose();
    } else {
      hud.refreshStatus();
    }
  }

  function pileOf(cardId: string): Pile | undefined {
    return findPileWithCard(state, cardId);
  }

  function undo() {
    cancelCascade();
    const prev = popHistory();
    if (!prev) return;
    state = prev;
    winShown = false;
    hud.hideOverlay();
    snapAll();
    hud.refreshStatus();
    refreshUndo();
  }

  function dealNew(seed?: number) {
    cancelCascade();
    for (const el of cardElById.values()) el.remove();
    cardElById.clear();
    history.length = 0;
    winShown = false;
    state = spec.rules.setup(shuffle(deckFactory(), seed));
    buildCards(Object.values(state.piles).flatMap((p) => p.cards));
    snapAll();
    hud.resetStats();
    hud.refreshStatus();
    refreshUndo();
  }

  function newGame() {
    dailyDate = null;
    dealNew();
  }

  function startDaily(date: string) {
    dailyDate = date;
    dealNew(seedForDateStr(date));
  }

  // ── drag plumbing ─────────────────────────────────────────────────
  // Track the original (left, top, z-index) of each dragged card so we
  // can snap back if the drop is invalid.
  const dragOrigin = new Map<HTMLElement, { left: string; top: string; z: string }>();

  function dragStackEls(picked: HTMLElement): HTMLElement[] {
    const id = picked.dataset.cardId!;
    const ids = spec.dragStackIds(state, id);
    return ids.map((i) => cardElById.get(i)).filter((el): el is HTMLDivElement => !!el);
  }

  function findDropPile(x: number, y: number, fromPile: Pile, cardId: string): string | null {
    const exclude = new Set([fromPile.id, ...spec.dropExclude()]);
    let bestLegal: { id: string; d: number } | null = null;
    let bestAny: { id: string; d: number } | null = null;
    for (const id of Object.keys(state.piles)) {
      if (exclude.has(id)) continue;
      const slot = spec.slotForPile(id);
      const pile = state.piles[id]!;
      const fan = slot.fanOffset ?? FAN_DOWN;
      const cy = slot.fanDown && pile.cards.length > 0
        ? slot.y + (pile.cards.length - 1) * fan
        : slot.y;
      const d = Math.hypot(x - slot.x, y - cy);
      if (d > CARD_W * 1.5) continue;
      if (!bestAny || d < bestAny.d) bestAny = { id, d };
      if (spec.rules.canMove(state, { from: fromPile.id, to: id, cardId })) {
        if (!bestLegal || d < bestLegal.d) bestLegal = { id, d };
      }
    }
    return bestLegal?.id ?? bestAny?.id ?? null;
  }

  function autoSend(cardId: string): boolean {
    const from = pileOf(cardId);
    if (!from) return false;
    for (const toId of spec.autoSendOrder()) {
      if (toId === from.id) continue;
      const move: Move = { from: from.id, to: toId, cardId };
      if (!spec.rules.canMove(state, move)) continue;
      const next = applyMove(state, move, spec.rules);
      if (!next) continue;
      pushHistory();
      state = next;
      snapAll();
      afterStateChange();
      return true;
    }
    return false;
  }

  attachClassicDrag({
    root: felt,
    pickAt: (target) => {
      let cur: HTMLElement | null = target as HTMLElement | null;
      while (cur && !(cur.classList && cur.classList.contains('cl2-card'))) {
        cur = cur.parentElement;
      }
      if (!cur) return null;
      const id = cur.dataset.cardId;
      if (!id) return null;
      // only allow picking the top card of a pile, OR the start of a
      // legitimate drag stack (both cases are validated by getDragStack)
      return cur;
    },
    getDragStack: dragStackEls,
    onPickStart: () => {},
    onDragStart(stack) {
      // remember origin and lift z so the dragged stack draws above others
      stack.forEach((el, i) => {
        dragOrigin.set(el, {
          left: el.style.left,
          top: el.style.top,
          z: el.style.zIndex,
        });
        el.style.zIndex = String(1000 + i);
        el.classList.add('cl2-card--dragging');
      });
    },
    onDragMove(lead, x, y) {
      const stack = Array.from(dragOrigin.keys());
      // place each card relative to its original offset from lead
      const leadOrig = dragOrigin.get(lead);
      if (!leadOrig) return;
      const baseLeft = parseFloat(leadOrig.left || '0');
      const baseTop = parseFloat(leadOrig.top || '0');
      for (const el of stack) {
        const orig = dragOrigin.get(el)!;
        const ox = parseFloat(orig.left || '0') - baseLeft;
        const oy = parseFloat(orig.top || '0') - baseTop;
        el.style.left = `${x + ox}px`;
        el.style.top = `${y + oy}px`;
      }
    },
    onDrop(lead, x, y) {
      const id = lead.dataset.cardId!;
      const from = pileOf(id);
      if (!from) return false;
      const toId = findDropPile(x, y, from, id);
      let committed = false;
      if (toId) {
        const move: Move = { from: from.id, to: toId, cardId: id };
        if (spec.rules.canMove(state, move)) {
          const next = applyMove(state, move, spec.rules);
          if (next) {
            pushHistory();
            state = next;
            committed = true;
          }
        }
      }
      // clear drag state on every card
      for (const el of dragOrigin.keys()) el.classList.remove('cl2-card--dragging');
      dragOrigin.clear();
      if (!committed) return false;
      snapAll();
      afterStateChange();
      return true;
    },
    onCancel(stack) {
      for (const el of stack) {
        const o = dragOrigin.get(el);
        if (!o) continue;
        el.style.left = o.left;
        el.style.top = o.top;
        el.style.zIndex = o.z;
        el.classList.remove('cl2-card--dragging');
      }
      dragOrigin.clear();
      snapAll();
    },
    onClick(el) {
      const id = el.dataset.cardId!;
      const pile = pileOf(id);
      if (!pile) return;
      if (!spec.onPileClick) return;
      pushHistory();
      const result = spec.onPileClick(state, pile, id);
      if (!result) {
        popHistory();
        return;
      }
      const opt = typeof result === 'object' ? result : {};
      const commit = opt.commit !== false;
      if (!commit) popHistory();
      snapAll();
      if (commit) afterStateChange();
      else hud.refreshStatus();
    },
    onDoubleClick(el) {
      const id = el.dataset.cardId!;
      const pile = pileOf(id);
      if (!pile || pile.type === 'stock') return;
      autoSend(id);
    },
  });

  // ── click-on-empty-pile (e.g. Spider deal, Klondike stock recycle) ─
  // The card-pointer-down path only fires on a card div; clicks on the
  // bare pile slot need a separate hook. We dispatch via spec.onPileClick
  // with the pile's own id in cardId-position when there are no cards.
  felt.addEventListener('click', (e) => {
    if (!spec.onPileClick) return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.classList.contains('cl2-card')) return; // handled by drag layer
    // hit-test the bare pile area by checking which slot the click landed in
    const rect = felt.getBoundingClientRect();
    const fx = e.clientX - rect.left;
    const fy = e.clientY - rect.top;
    for (const pile of Object.values(state.piles)) {
      if (pile.cards.length > 0) continue;
      const slot = spec.slotForPile(pile.id);
      if (fx >= slot.x && fx <= slot.x + CARD_W && fy >= slot.y && fy <= slot.y + CARD_H) {
        pushHistory();
        const result = spec.onPileClick(state, pile, pile.id);
        if (!result) {
          popHistory();
          return;
        }
        const opt = typeof result === 'object' ? result : {};
        const commit = opt.commit !== false;
        if (!commit) popHistory();
        snapAll();
        if (commit) afterStateChange();
        else hud.refreshStatus();
        return;
      }
    }
  });
}

// Local clone helper (cheap deep-copy of the state - matches engine's
// cloneState contract: piles reformed, cards copied by value).
function cloneState(state: GameState): GameState {
  const piles: GameState['piles'] = {};
  for (const [id, pile] of Object.entries(state.piles)) {
    piles[id] = {
      id: pile.id,
      type: pile.type,
      cards: pile.cards.map((c) => ({ ...c })),
    };
  }
  return { piles, movesCount: state.movesCount };
}
